import {
  DiscountPageSchema,
  DiscountSchema,
  PricePreviewSchema,
  PromotionListSchema,
  PromotionSchema,
  type CreateDiscountRequest,
  type PreviewRequest,
  type SavePromotionRequest,
  type UpdateDiscountRequest,
} from '@deli/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getJson, sendJson } from './client.js';

export const DISCOUNT_PAGE = 50;

export const useDiscounts = (filter: { customerId?: string; offset?: number }) =>
  useQuery({
    queryKey: ['pricing', 'discounts', filter],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ limit: String(DISCOUNT_PAGE) });
      if (filter.customerId) params.set('customerId', filter.customerId);
      if (filter.offset) params.set('offset', String(filter.offset));
      return getJson(`/api/v1/pricing/discounts?${params}`, DiscountPageSchema, { signal });
    },
    placeholderData: keepPreviousData,
  });

export const usePromotions = () =>
  useQuery({
    queryKey: ['pricing', 'promotions'],
    queryFn: ({ signal }) => getJson('/api/v1/pricing/promotions', PromotionListSchema, { signal }),
  });

function usePricingMutation<T, R>(fn: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: ['pricing'] }),
  });
}

export type SaveDiscount =
  { id: null; body: CreateDiscountRequest } | { id: string; body: UpdateDiscountRequest };

export const useSaveDiscount = () =>
  usePricingMutation(({ id, body }: SaveDiscount) =>
    id === null
      ? sendJson('POST', '/api/v1/pricing/discounts', body, DiscountSchema)
      : sendJson('PUT', `/api/v1/pricing/discounts/${id}`, body, DiscountSchema),
  );

export const useDeleteDiscount = () =>
  usePricingMutation(({ id, reason }: { id: string; reason: string }) =>
    sendJson('DELETE', `/api/v1/pricing/discounts/${id}`, { reason }, null),
  );

export const useSavePromotion = () =>
  usePricingMutation(({ id, body }: { id: string | null; body: SavePromotionRequest }) =>
    id === null
      ? sendJson('POST', '/api/v1/pricing/promotions', body, PromotionSchema)
      : sendJson('PUT', `/api/v1/pricing/promotions/${id}`, body, PromotionSchema),
  );

export const useDeletePromotion = () =>
  usePricingMutation(({ id, reason }: { id: string; reason: string }) =>
    sendJson('DELETE', `/api/v1/pricing/promotions/${id}`, { reason }, null),
  );

/** Prices a sample cart on the server (the UI never does price arithmetic). Stores nothing. */
export const usePreview = () =>
  useMutation({
    mutationFn: (body: PreviewRequest) =>
      sendJson('POST', '/api/v1/pricing/preview', body, PricePreviewSchema),
  });
